/* ── Service Mode (staff / QA paywall bypass) ──────────────────────
 * When the operator enters SERVICE_MODE_CODE in the registration field
 * on the landing page, the backend returns a `svc_*` session id that
 * unlocks every paywall (verdict £1, premium PDF £4.99, seller call
 * £2.99) for ANY registration in the current browser session — no real
 * Stripe charge is ever made.
 *
 * Design:
 *  · The active svc session id lives in localStorage under
 *    `worthifyServiceModeSession` so it survives navigation but not a
 *    private/incognito window.
 *  · `getPaidSession(reg)` (see paidSessions.js) falls back to this
 *    session when no per-reg paid entry exists, so every downstream
 *    hook (useAnalyzeFlow, usePremiumData, usePaymentFlow, NinaCopilot)
 *    automatically sees the unlock — no changes needed there.
 *  · Backend session has a 24 h TTL matching the regular £4.99 unlock.
 */

const SERVICE_MODE_KEY = "worthifyServiceModeSession";
const SERVICE_MODE_TTL_MS = 24 * 60 * 60 * 1000; // 24 h — match backend

/** Cheap client-side sniff. The real check happens server-side. */
export const isServiceModeCode = (input) => {
  if (!input || typeof input !== "string") return false;
  return input.trim().toUpperCase().startsWith("WSVC-");
};

export const getServiceModeSession = () => {
  try {
    const raw = localStorage.getItem(SERVICE_MODE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.sessionId || !parsed.activatedAt) return null;
    if (Date.now() - parsed.activatedAt > SERVICE_MODE_TTL_MS) {
      localStorage.removeItem(SERVICE_MODE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const clearServiceMode = () => {
  try { localStorage.removeItem(SERVICE_MODE_KEY); } catch { /* ignore */ }
};

/**
 * POST /api/service-mode/verify — returns {ok:true, sessionId} on match,
 * {ok:false, status, message} on failure.
 */
export const verifyServiceCode = async (code, apiBase) => {
  try {
    const r = await fetch(`${apiBase}/service-mode/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code: (code || "").trim() }),
    });
    if (r.status === 403) return { ok: false, status: 403, message: "Invalid code" };
    if (r.status === 429) return { ok: false, status: 429, message: "Too many attempts — try again later" };
    if (r.status === 503) return { ok: false, status: 503, message: "Service mode disabled" };
    if (!r.ok) return { ok: false, status: r.status, message: "Verification failed" };
    const j = await r.json();
    if (!j || !j.valid || !j.session_id) return { ok: false, status: 500, message: "Malformed response" };
    try {
      localStorage.setItem(SERVICE_MODE_KEY, JSON.stringify({
        sessionId: j.session_id,
        activatedAt: Date.now(),
      }));
    } catch { /* localStorage blocked — session_id still usable in-memory */ }
    return { ok: true, sessionId: j.session_id };
  } catch (e) {
    return { ok: false, status: 0, message: "Network error" };
  }
};
